/* Meta Event Match Quality — enriched browsing events.
 *
 * Fires bs_vc and bs_atc as custom events through the pixel the Facebook &
 * Instagram app already loads, adding the hashed email and phone those events
 * otherwise lack (measured 0.8% and 0.2% coverage, against 100% on Purchase).
 *
 * This lives in an external asset rather than inline in the layout. An earlier
 * inline version appeared in the served HTML but not in the browser DOM, and
 * never executed. The cause was not established — the Nitro X optimiser was
 * suspected but never proven, and stale rendered HTML after a theme push
 * produces the same symptom. Treat the cause as open.
 *
 * Liquid values arrive as data attributes on the script tag rather than as an
 * inline config object, so the config cannot be lost independently of the code.
 */
(function () {
  'use strict';

  var tag = document.getElementById('bs-meta-events');
  if (!tag) return;

  var cfg = tag.dataset || {};
  var PIXEL_ID = cfg.pixel;
  if (!PIXEL_ID) return;

  var LS_KEY    = 'bs_meta_identity_v1';
  var FBC_DAYS  = 90;
  var DEDUPE_MS = 1500;
  var CURRENCY  = cfg.currency || 'INR';

  /* ------------------------------------------------------------------ */
  /* storage                                                             */
  /* ------------------------------------------------------------------ */

  function readCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]*)');
    return m ? decodeURIComponent(m[2]) : null;
  }

  function writeCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() +
                      ';path=/;SameSite=Lax';
  }

  /* KwikPass uses a cookie on some flows and localStorage on others. */
  function readCookieOrStorage(name) {
    var v = readCookie(name);
    if (v) return v;
    try { return window.localStorage.getItem(name); } catch (e) { return null; }
  }

  /* The verified phone from KwikPass.
   *
   * The implementation brief points at `notify_phone_number` (citing
   * snippets/kwikpass.liquid:44) as the phone source. Reading that key directly
   * does not work, and would have made this whole feature a silent no-op for
   * `ph`. KwikPass's own getNotifyPh() shows why:
   *
   *     let o = kpGetLocalStorageValue("notify_phone_number")
   *          || kpGetCookieValue("notify_phone_number");
   *     if (o) { ... setValueInCookiesAndLocalStorage("notifyph", btoa(o)),
   *              deleteValueFromCookiesAndLocalStorage("notify_phone_number") ... }
   *     const t = getValueFromCookiesOrLocalStorage("notifyph");
   *     if (t) { const e = atob(t).split("|"); ... }
   *
   * `notify_phone_number` is transient: on first sight KwikPass base64-encodes
   * it into `notifyph` and deletes the original. The durable value is
   * `notifyph`, holding base64 of "+91|9876543210" (country code, pipe, number)
   * — or occasionally just the number with no pipe.
   *
   * So read `notifyph` first and fall back to the transient key for the brief
   * window before migration. normalisePhone() strips the "+" and the pipe. */
  function readVerifiedPhone() {
    var encoded = readCookieOrStorage('notifyph');
    if (encoded) {
      try {
        var decoded = window.atob(encoded);
        if (decoded) return decoded.replace('|', '');
      } catch (e) { /* not base64 — fall through */ }
    }
    return readCookieOrStorage('notify_phone_number');
  }

  /* ------------------------------------------------------------------ */
  /* normalisation — Meta rejects badly formatted values silently        */
  /* ------------------------------------------------------------------ */

  function normaliseEmail(v) {
    return String(v).trim().toLowerCase();
  }

  /* India: strip non-numerics, drop leading zeros, ensure a leading 91.
       9876543210      -> 919876543210
       +91 98765 43210 -> 919876543210
       09876543210     -> 919876543210 */
  function normalisePhone(v) {
    var d = String(v).replace(/\D/g, '').replace(/^0+/, '');
    if (d.length === 10) return '91' + d;
    if (d.length === 13 && d.indexOf('910') === 0) return '91' + d.slice(3);
    return d;
  }

  function sha256Hex(value) {
    var bytes = new TextEncoder().encode(value);
    return window.crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      var out = '';
      var view = new Uint8Array(buf);
      for (var i = 0; i < view.length; i++) {
        out += ('0' + view[i].toString(16)).slice(-2);
      }
      return out;
    });
  }

  /* ------------------------------------------------------------------ */
  /* fbc                                                                 */
  /* ------------------------------------------------------------------ */

  /* Read fbclid from the raw query string. URLSearchParams.get() decodes the
     value and Meta expects the exact raw string — decoding it is a silent
     match failure. */
  function rawQueryParam(name) {
    var q = window.location.search.replace(/^\?/, '');
    if (!q) return null;
    var parts = q.split('&');
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      if (eq > 0 && parts[i].slice(0, eq) === name) return parts[i].slice(eq + 1);
    }
    return null;
  }

  function ensureFbc() {
    if (readCookie('_fbc')) return;
    var clid = rawQueryParam('fbclid');
    if (!clid) return;
    writeCookie('_fbc', 'fb.1.' + Date.now() + '.' + clid, FBC_DAYS);
  }

  /* ------------------------------------------------------------------ */
  /* persisted identity                                                  */
  /* ------------------------------------------------------------------ */

  /* Most browsing is logged out, so identifiers seen once are stored and
     replayed later. Coverage compounds week over week. */

  var CUSTOMER_ID    = cfg.customerId || '';
  var CUSTOMER_EMAIL = cfg.customerEmail || '';

  function loadIdentity() {
    try { return JSON.parse(window.localStorage.getItem(LS_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function saveIdentity(obj) {
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch (e) {}
  }

  function uuid() {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* The single place external_id is decided.

     The Facebook app reports 100% external_id coverage, but that comes from its
     server-side stream — its browser pixel sends none at all (verified in the
     live /tr requests), so there is no browser-side value to match against. If
     the server value is ever identified, change it here and nowhere else. */
  function externalId(identity) {
    if (CUSTOMER_ID) return String(CUSTOMER_ID);
    if (!identity.xid) identity.xid = uuid();
    return identity.xid;
  }

  function resolveIdentity() {
    var identity = loadIdentity();
    var pending  = [];

    identity.xid = externalId(identity);

    if (CUSTOMER_EMAIL) {
      pending.push(sha256Hex(normaliseEmail(CUSTOMER_EMAIL)).then(function (h) {
        identity.em = h;
      }));
    }

    var phone = readVerifiedPhone();
    if (phone) {
      var normalised = normalisePhone(phone);
      if (normalised.length >= 11) {
        pending.push(sha256Hex(normalised).then(function (h) { identity.ph = h; }));
      }
    }

    return Promise.all(pending).then(function () {
      saveIdentity(identity);
      return identity;
    }).catch(function () {
      return identity;
    });
  }

  /* ------------------------------------------------------------------ */
  /* sending                                                             */
  /* ------------------------------------------------------------------ */

  var lastSent = {};

  /* fbq is defined by Shopify's web pixel runtime, which loads asynchronously —
     at DOMContentLoaded it frequently does not exist yet. Returning early when
     it is missing drops events intermittently, depending purely on load timing.
     Wait for it, and give up after ~15s rather than polling forever. */
  function whenFbqReady(callback) {
    if (typeof window.fbq === 'function') { callback(); return; }
    var tries = 0;
    var timer = setInterval(function () {
      if (typeof window.fbq === 'function') {
        clearInterval(timer);
        callback();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 150);
  }

  function send(eventName, params, dedupeKey) {
    if (dedupeKey) {
      var now = Date.now();
      if (lastSent[dedupeKey] && now - lastSent[dedupeKey] < DEDUPE_MS) return;
      lastSent[dedupeKey] = now;
    }

    ensureFbc();

    resolveIdentity().then(function (identity) {
      /* Advanced matching. Re-calling init on an already-initialised pixel
         merges user data into it; it does not register a second pixel and does
         not fire PageView. fbp and fbc are attached by the pixel itself.

         Note this is pixel-level, not event-level: once set, the same user data
         rides on every subsequent event on this pixel, including the Facebook
         app's own standard ViewContent and AddToCart. Meta offers no per-event
         user data for browser pixels, so it cannot be scoped to our events. */
      var userData = { external_id: identity.xid };
      if (identity.em) userData.em = identity.em;
      if (identity.ph) userData.ph = identity.ph;

      whenFbqReady(function () {
        try {
          window.fbq('init', PIXEL_ID, userData);
          window.fbq('trackCustom', eventName, params);
        } catch (e) {}
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* bs_vc — product pages                                               */
  /* ------------------------------------------------------------------ */

  if (cfg.vcId) {
    send('bs_vc', {
      content_ids: [Number(cfg.vcId) || cfg.vcId],
      content_type: 'product',
      value: Number(cfg.vcValue) || 0,
      currency: CURRENCY
    });
  }

  /* ------------------------------------------------------------------ */
  /* bs_atc — every add-to-cart path                                     */
  /* ------------------------------------------------------------------ */

  /* This theme adds to cart from at least five places: three call sites in
     theme.js, one in global.js, one in page-sticky-product-bar.js, plus native
     form posts in the Frequently Bought Together snippet and the wishlist page.
     Hooking each is how a path gets silently missed, so intercept the network
     layer — that covers every current path and any added later. */

  function isCartAdd(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  /* /cart/add.js responds with the added line item, carrying variant id, price
     in minor units and quantity — more reliable than parsing whatever shape the
     request body happened to take at each call site. */
  function sendFromCartAddResponse(text) {
    var data;
    try { data = JSON.parse(text); } catch (e) { return; }

    var items = data && data.items ? data.items : [data];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.id) continue;
      var quantity = item.quantity || 1;
      send('bs_atc', {
        content_ids: [item.id],
        content_type: 'product',
        value: ((item.price || 0) / 100) * quantity,
        currency: CURRENCY
      }, 'atc:' + item.id);
    }
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var result = nativeFetch.apply(this, arguments);
      if (isCartAdd(url)) {
        result.then(function (response) {
          if (!response || !response.ok) return;
          response.clone().text().then(sendFromCartAddResponse).catch(function () {});
        }).catch(function () {});
      }
      return result;
    };
  }

  var nativeOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (nativeOpen) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartAdd(url)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            sendFromCartAddResponse(this.responseText);
          }
        });
      }
      return nativeOpen.apply(this, arguments);
    };
  }

  /* Native (non-AJAX) form posts navigate away, so read the variant off the
     form and fire before unload. Value is omitted — the price is not reliably
     in the form, and a wrong value is worse than none. Deduped against the
     AJAX path above. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    var field = form.querySelector('[name="id"]');
    if (!field || !field.value) return;
    send('bs_atc', {
      content_ids: [Number(field.value) || field.value],
      content_type: 'product',
      currency: CURRENCY
    }, 'atc:' + field.value);
  }, true);
})();
